import { type KeyboardEvent, useMemo, useRef, useState } from 'react';
import { type ReportView, updateReportSchema } from '#shared/types';
import { useFormSubmit } from '#client/hooks/useFormSubmit';
import { type CultureDraft, cultureProgress, initialCulture } from './culture-entry.logic';
import {
  type DraftDetails,
  buildEntryPayloads,
  calculatedIds,
  countEntered,
  entryKeyIntent,
  initialDetails,
  withCalculated,
  withRange,
  withResult,
} from './result-entry.logic';
import { saveReport } from './report.service';

/**
 * Everything about entering results that is not markup. Composed from three
 * smaller hooks — the result draft and its progress (`useResultDraft`), the
 * remark and comment selections (`useEntryNotes`) and keyboard navigation
 * (`useEntryKeyboard`) — plus the reporting doctor, the save and its payloads.
 *
 * Extracted so the screen itself is a layout, and so the rules here — what
 * "complete" means, what Enter does — can be read without scrolling past a
 * table.
 */
export function useResultEntry(report: ReportView, onSaved: () => void) {
  // The reporting doctor. Defaulted onto the report from its lab test at
  // registration, and changeable here until the report completes — the one
  // attribution the request body may set. Who *performed* and who *verified*
  // come from the session, because a request that could name them could forge
  // attribution on a clinical document.
  const [consultantId, setConsultantId] = useState<number | null>(report.consultant_id);

  const notes = useEntryNotes(report);
  const draft = useResultDraft(report);
  const { details, culture, progress } = draft;
  const [expanded, setExpanded] = useState<number | null>(null);

  const detailById = useMemo(
    () => new Map(report.report_details.map((detail) => [detail.id, detail])),
    [report.report_details],
  );

  const form = useFormSubmit({
    schema: updateReportSchema,
    submit: (input) => saveReport(report.id, input),
    successMessage: 'Report saved',
    onSuccess: onSaved,
  });

  const payloads = buildEntryPayloads({
    remark_ids: notes.remarkIds,
    comment_ids: notes.commentIds,
    details,
    consultant_id: consultantId,
    culture,
  });

  const keyboard = useEntryKeyboard({
    // Every row, calculated ones included: field indices run over all of them.
    total: details.length,
    completeIfReady: (event, canEdit) => {
      if (progress.allEntered && canEdit) form.handleSubmit(payloads.complete)(event);
    },
  });

  return {
    form,
    details,
    culture,
    setCulture: draft.setCulture,
    detailById,
    notes,
    consultantId,
    setConsultantId,
    expanded,
    setExpanded,
    setResult: draft.setResult,
    setValueId: draft.setValueId,
    ...progress,
    draftPayload: payloads.draft,
    completePayload: payloads.complete,
    reopenPayload: payloads.reopen,
    ...keyboard,
  };
}

/** Everything the entry screen reads from `useResultEntry`. */
export type ResultEntry = ReturnType<typeof useResultEntry>;

/**
 * The working copy of the results and how far through them it is.
 *
 * Details are addressed by id and updated in place; replacing the whole array
 * would discard ids and lose entered results. Calculated rows (MCV, eGFR…) are
 * recomputed on every keystroke, so they read the same here as the server will
 * store. A culture report records its findings in `culture` instead.
 */
function useResultDraft(report: ReportView) {
  const [details, setDetails] = useState<DraftDetails>(() =>
    withCalculated(initialDetails(report), report),
  );
  const calculated = useMemo(() => calculatedIds(report), [report]);
  const [culture, setCulture] = useState<CultureDraft | null>(() =>
    report.culture ? initialCulture(report.culture) : null,
  );

  return {
    details,
    culture,
    setCulture,
    progress: culture ? cultureProgress(culture) : countEntered(details, calculated),
    setResult: (detailId: number, result: string) =>
      setDetails((current) => withCalculated(withResult(current, detailId, result), report)),
    setValueId: (detailId: number, valueId: number) =>
      setDetails((current) => withRange(current, detailId, valueId)),
  };
}

/** The remark and comment selections, both in print order — the order the server stores. */
function useEntryNotes(report: Pick<ReportView, 'remarks' | 'comments'>) {
  const [remarkIds, setRemarkIds] = useState<number[]>(report.remarks.map((remark) => remark.id));
  const [commentIds, setCommentIds] = useState<number[]>(
    report.comments.map((comment) => comment.id),
  );
  return { remarkIds, setRemarkIds, commentIds, setCommentIds };
}

/**
 * Keyboard movement between result fields.
 *
 * Enter goes forward, Shift+Enter back; past the last field, focus lands on
 * Save so the natural next keystroke completes the task.
 */
function useEntryKeyboard({
  total,
  completeIfReady,
}: {
  total: number;
  completeIfReady: (event: KeyboardEvent, canEdit: boolean) => void;
}) {
  /** One ref per row, so Enter can move focus to the next result field. */
  const inputRefs = useRef<(HTMLInputElement | HTMLSelectElement | null)[]>([]);
  const saveRef = useRef<HTMLButtonElement>(null);

  // Calculated rows have no field, so movement steps over them.
  const focusField = (index: number, step: 1 | -1) => {
    let next = index;
    while (next >= 0 && next < total && !inputRefs.current[next]) next += step;
    const target = inputRefs.current[next];
    if (target) {
      target.focus();
      if (target instanceof HTMLInputElement) target.select();
    } else if (next >= total) {
      saveRef.current?.focus();
    }
  };

  const makeKeyDownHandler = (index: number, canEdit: boolean) => (event: KeyboardEvent) => {
    const intent = entryKeyIntent(event, index);
    if (intent === null) return;
    // The important line on this screen. A bare Enter inside a form submits
    // it; here it must advance instead, or a technician loses the report
    // halfway through entering it.
    event.preventDefault();
    if (intent.kind === 'complete') completeIfReady(event, canEdit);
    else focusField(intent.index, intent.index > index ? 1 : -1);
  };

  return { inputRefs, saveRef, makeKeyDownHandler };
}
